import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ActivatedRoute, Router } from '@angular/router';
import { Toast } from "primeng/toast";
import { CommonModule } from '@angular/common';
import { MenuModule } from 'primeng/menu';
import { OciService } from '../../services/oci.service';
import { ProjectFileFolderModel, ProjectFileModel } from '../../models/projectfile.model';
import { ProjectfileService } from '../../services/projectfile.service';
import { ProjectStateService } from '../../services/project-state.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { AuthService } from '../../core/services/auth.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';
import { ProjectModel } from '../../models/project.model';
import { buildProjectAssetDownloadFileName, buildProjectFolderZipFileName } from '../../utils/download-file-name.utils';

@Component({
  selector: 'app-plans',
  imports: [MenuModule,
    DialogModule,
    ButtonModule,
    FileUploadModule,
    FormsModule,
    InputTextModule,
    Toast,
    CommonModule,
    ConfirmDialogModule],
  providers: [ConfirmationService],
  templateUrl: './projectfiles.html',
  styleUrls: ['./projectfiles.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectfilesComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  showAddFileFolderDialog: boolean = false;
  showAddFileDialog: boolean = false;
  projectId: number = 0;
  folderList: ProjectFileFolderModel[] = [];
  filteredFolderList: ProjectFileFolderModel[] = [];
  selectedFolders: any[] = [];
  @ViewChild(FileUpload) fileUpload!: FileUpload;
  selectedFiles: any[] = [];
  projectFileFolderModel: ProjectFileFolderModel = {} as ProjectFileFolderModel;
  projectFileModel: ProjectFileModel = {} as ProjectFileModel;
  isReadOnly: boolean = false;
  canView: boolean = false;
  canUpload: boolean = false;
  canDelete: boolean = false;
  isProjectInactive: boolean = false;
  showEditFileNameDialog: boolean = false;
  editingFile: ProjectFileModel | null = null;
  editFileName: string = '';
  selectedFileIds: Set<number> = new Set();
  selectedFolderIds: Set<number> = new Set();
  isUploading: boolean = false;
  readonly maxProjectFileSizeBytes = 20 * 1024 * 1024;
  readonly maxProjectFileSizeLabel = '20 MB';
  uploadTotalFiles: number = 0;
  uploadCurrentFileNumber: number = 0;
  uploadCompletedFiles: number = 0;
  uploadFailedFiles: number = 0;
  private uploadFileStatuses = new Map<string, 'pending' | 'uploading' | 'completed' | 'error'>();
  private dataLoaded = false;
  collapsedFolders: Set<number> = new Set();
  currentProject: ProjectModel | null = null;
  actionItems: MenuItem[] = [
    {
      label: 'Delete',
      icon: 'ri-delete-bin-line',
      styleClass: 'danger-menu-item',
      command: () => this.deleteSelected()
    }
  ];
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: ProjectfileService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private ociService: OciService,
    private projectStateService: ProjectStateService,
    private permissionHelper: PermissionHelperService,
    private confirmationService: ConfirmationService,
    private authService: AuthService
  ) { }
  ngOnInit(): void {
    this.projectStateService.currentProject$.pipe(takeUntil(this.destroy$)).subscribe(project => {
      this.currentProject = project;
      this.cdr.markForCheck();
    });

    // Extract projectId from URL parameters
    this.route.paramMap.subscribe(params => {
      const projectIdParam = params.get('projectId');
      if (projectIdParam) {
        this.projectId = parseInt(projectIdParam);
        console.log('Project ID from URL:', this.projectId);

        if (this.projectId > 0) {
          this.tryLoadFiles();
        } else {
          console.error('Invalid project ID:', this.projectId);
        }
      } else {
        console.error('No project ID found in URL parameters');
      }
    });

    this.projectStateService.pageSearch$.pipe(takeUntil(this.destroy$)).subscribe(search => {
      this.applySearch(search);
    });

    this.projectStateService.currentProjectUserActive$.subscribe(isActive => {
      this.isProjectInactive = isActive === false;
      this.updateReadOnlyState();
    });

    this.projectStateService.currentProjectPermissions$.subscribe(() => {
      this.canView = this.permissionHelper.has(PAGE_KEYS.FILES, PERMISSION_KEYS.VIEW);
      this.canUpload = this.permissionHelper.has(PAGE_KEYS.FILES, PERMISSION_KEYS.UPLOAD);
      this.canDelete = this.permissionHelper.has(PAGE_KEYS.FILES, PERMISSION_KEYS.DELETE);
      this.updateReadOnlyState();
      this.tryLoadFiles();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applySearch(search: string): void {
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      this.filteredFolderList = [...this.folderList];
    } else {
      this.filteredFolderList = this.folderList.filter(folder =>
        (folder.folderName || '').toLowerCase().includes(term) ||
        (folder.projectFileList || []).some(f =>
          (f.fileName || f.fileFullName || '').toLowerCase().includes(term)
        )
      );
    }
    this.cdr.markForCheck();
  }

  private updateReadOnlyState(): void {
    this.isReadOnly = this.isProjectInactive || (!this.canUpload && !this.canDelete);
    this.cdr.markForCheck();
  }

  private tryLoadFiles(): void {
    if (this.dataLoaded || !this.projectId || this.projectId <= 0) return;
    if (!this.canView) return;
    this.getProjectFiles();
    this.dataLoaded = true;
  }
  getProjectFiles(): void {
    this.service.getProjectFiles(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (folders) => {
        this.folderList = folders;
        this.collapsedFolders = new Set(folders.map(f => f.fileFolderId));
        this.applySearch(this.projectStateService.getPageSearch());
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error fetching project files:', error);
        this.cdr.markForCheck();
      }
    });
  }

  toggleFolderCollapse(folderId: number): void {
    if (this.collapsedFolders.has(folderId)) {
      this.collapsedFolders.delete(folderId);
    } else {
      this.collapsedFolders.add(folderId);
    }
  }

  isFolderCollapsed(folderId: number): boolean {
    return this.collapsedFolders.has(folderId);
  }

  getFileIcon(file: ProjectFileModel): string {
    const ext = this.getFileExtensionFromName(file.fileName || file.fileFullName || '');
    if (['xlsx', 'xls'].includes(ext)) return 'ri-file-excel-2-line';
    if (['docx', 'doc'].includes(ext)) return 'ri-file-word-2-line';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'ri-image-line';
    if (ext === 'pdf') return 'ri-file-pdf-2-line';
    return 'ri-file-line';
  }

  getFileIconBg(file: ProjectFileModel): string {
    const ext = this.getFileExtensionFromName(file.fileName || file.fileFullName || '');
    if (['xlsx', 'xls'].includes(ext)) return '#ebf5ec';
    if (['docx', 'doc'].includes(ext)) return '#ecf6fd';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return '#fff7e0';
    if (ext === 'pdf') return '#fee7e7';
    return '#f4f4f6';
  }

  getFileIconColor(file: ProjectFileModel): string {
    const ext = this.getFileExtensionFromName(file.fileName || file.fileFullName || '');
    if (['xlsx', 'xls'].includes(ext)) return '#1d6f42';
    if (['docx', 'doc'].includes(ext)) return '#2b579a';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return '#e09500';
    if (ext === 'pdf') return '#eb4747';
    return '#606576';
  }

  getUploadFileIcon(file: File): string {
    const ext = this.getFileExtensionFromName(file?.name || '');
    if (['xlsx', 'xls'].includes(ext)) return 'ri-file-excel-2-line';
    if (['docx', 'doc'].includes(ext)) return 'ri-file-word-2-line';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'ri-image-line';
    if (ext === 'pdf') return 'ri-file-pdf-2-line';
    return 'ri-file-line';
  }

  getUploadFileIconBg(file: File): string {
    const ext = this.getFileExtensionFromName(file?.name || '');
    if (['xlsx', 'xls'].includes(ext)) return '#ebf5ec';
    if (['docx', 'doc'].includes(ext)) return '#ecf6fd';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return '#fff7e0';
    if (ext === 'pdf') return '#fee7e7';
    return '#f4f4f6';
  }

  getUploadFileIconColor(file: File): string {
    const ext = this.getFileExtensionFromName(file?.name || '');
    if (['xlsx', 'xls'].includes(ext)) return '#1d6f42';
    if (['docx', 'doc'].includes(ext)) return '#2b579a';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return '#e09500';
    if (ext === 'pdf') return '#eb4747';
    return '#606576';
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes <= 0) return '0 Bytes';
    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, unitIndex);
    return `${value.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
  }

  removeSelectedFile(event: Event, index: number, removeFileCallback: (index: number) => void): void {
    event.stopPropagation();
    if (this.isUploading) {
      return;
    }
    removeFileCallback(index);
    this.selectedFiles = this.selectedFiles.filter((_, fileIndex) => fileIndex !== index);
    this.resetUploadProgress();
    this.cdr.markForCheck();
  }

  private getFileExtensionFromName(fileName: string): string {
    const name = (fileName || '').trim();
    if (!name.includes('.')) return '';
    return name.split('.').pop()?.toLowerCase() || '';
  }

  addPlanFolder() {
    if (!this.canUpload || this.isProjectInactive) {
      return;
    }
    // Implementation for adding a plan folder
      const folderName = this.projectFileFolderModel.folderName?.trim();
      if (!folderName) {
        this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Folder name cannot be empty.' });
        return;
      }
      // Check for duplicate folder name (case-insensitive)
      const duplicate = this.folderList.some((f: ProjectFileFolderModel) => f.folderName?.trim().toLowerCase() === folderName.toLowerCase());
      if (duplicate) {
        this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Folder name already exists.' });
        return;
      }
    this.projectFileFolderModel.projectId = this.projectId;
    // Call the service to add the file folder
    this.service.addProjectFileFolder(this.projectFileFolderModel).subscribe({
      next: (response) => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File folder added successfully.' });
        this.showAddFileFolderDialog = false;
        this.getProjectFiles();
        this.projectFileFolderModel = {} as ProjectFileFolderModel;
        // Refresh the file folders list or perform other actions as needed
      },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add file folder.' });
      }
    });
  }

  selectedFolder: ProjectFileFolderModel | null = null;
  onAddFileToFolder(folder: ProjectFileFolderModel) {
    if (!this.canUpload || this.isProjectInactive || this.isUploading) {
      return;
    }
    this.selectedFolder = folder;
    console.log('Adding file to folder:', folder);
    this.projectFileModel.fileFolderId = folder.fileFolderId;
    this.selectedFiles = [];
    this.resetUploadProgress();
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
    this.showAddFileDialog = true;
  }

  async addProjectFile() {
    if (!this.canUpload || this.isProjectInactive || this.isUploading) {
      return;
    }
    console.log('Uploading files to folder ID:', this.selectedFolder?.fileFolderId);
    console.log(this.selectedFiles);
    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'No files selected.' });
      return;
    }

    const filesToUpload = [...this.selectedFiles] as File[];
    const oversizedFile = filesToUpload.find(file => this.isFileOverSizeLimit(file));
    if (oversizedFile) {
      this.showFileSizeValidation(oversizedFile);
      return;
    }

    this.isUploading = true;
    this.uploadTotalFiles = filesToUpload.length;
    this.uploadCurrentFileNumber = 0;
    this.uploadCompletedFiles = 0;
    this.uploadFailedFiles = 0;
    this.uploadFileStatuses.clear();
    filesToUpload.forEach((file) => this.uploadFileStatuses.set(this.getUploadFileKey(file), 'pending'));
    this.cdr.markForCheck();
    let lastUploadErrorMessage = '';

    for (let index = 0; index < filesToUpload.length; index++) {
      const file = filesToUpload[index];
      const fileKey = this.getUploadFileKey(file);
      this.uploadCurrentFileNumber = index + 1;
      this.uploadFileStatuses.set(fileKey, 'uploading');
      this.cdr.markForCheck();

      const formData = new FormData();
      formData.append('projectFileData', JSON.stringify({
        projectId: this.projectId,
        fileFolderId: this.selectedFolder?.fileFolderId
      }));
      formData.append('files', file);

      try {
        await firstValueFrom(this.service.addProjectFile(formData));
        this.uploadCompletedFiles += 1;
        this.uploadFileStatuses.set(fileKey, 'completed');
      } catch (error) {
        this.uploadFailedFiles += 1;
        this.uploadFileStatuses.set(fileKey, 'error');
        lastUploadErrorMessage = this.getUploadErrorMessage(error);
        console.error('Project file upload failed:', file.name, error);
      }

      this.cdr.markForCheck();
    }

    this.isUploading = false;
    const failedFiles = filesToUpload.filter(file => this.uploadFileStatuses.get(this.getUploadFileKey(file)) === 'error');

    if (this.uploadCompletedFiles > 0) {
      this.getProjectFiles();
    }

    if (failedFiles.length === 0) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File(s) uploaded successfully.' });
        this.showAddFileDialog = false;
        this.projectFileModel = {} as ProjectFileModel;
        this.selectedFiles = [];
        this.selectedFolder = null;
        this.resetUploadProgress();
        if (this.fileUpload) {
          this.fileUpload.clear();
        }
    } else {
      this.selectedFiles = failedFiles;
      this.syncFileUploadFiles(failedFiles);
      this.messageService.add({
        severity: this.uploadCompletedFiles > 0 ? 'warn' : 'error',
        summary: this.uploadCompletedFiles > 0 ? 'Partial Upload' : 'Error',
        detail: `${this.uploadCompletedFiles} file(s) uploaded. ${failedFiles.length} file(s) failed. ${lastUploadErrorMessage || 'Retry the failed file(s).'}`
      });
    }

    this.cdr.markForCheck();
  }

  // FileUpload event handlers
  onFileSelect(event: any) {
    if (!this.canUpload || this.isProjectInactive || this.isUploading) {
      return;
    }
    const files = this.getCurrentUploadFiles(event);
    const validFiles = files.filter(file => !this.isFileOverSizeLimit(file));
    files.filter(file => this.isFileOverSizeLimit(file)).forEach(file => this.showFileSizeValidation(file));
    this.selectedFiles = validFiles;
    this.syncFileUploadFiles(validFiles);
    this.resetUploadProgress();
    this.cdr.markForCheck();
  }

  onFileValidationFail(event: any): void {
    const file = event?.file || event?.files?.[0] || event;
    if (file?.name) {
      this.showFileSizeValidation(file);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: `Each file must be ${this.maxProjectFileSizeLabel} or smaller.`
      });
    }
  }

  onFileRemove(event: any) {
    if (!this.canUpload || this.isProjectInactive || this.isUploading) {
      return;
    }
    this.selectedFiles = this.getCurrentUploadFiles(event);
    this.resetUploadProgress();
    this.cdr.markForCheck();
  }

  onFileClear() {
    if (!this.canUpload || this.isProjectInactive || this.isUploading) {
      return;
    }
    this.selectedFiles = [];
    this.resetUploadProgress();
    this.cdr.markForCheck();
  }

  cancelUploadDialog(): void {
    if (this.isUploading) {
      return;
    }
    this.showAddFileDialog = false;
    this.selectedFiles = [];
    this.selectedFolder = null;
    this.resetUploadProgress();
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
    this.cdr.markForCheck();
  }

  private getCurrentUploadFiles(event: any): File[] {
    const files = event?.currentFiles || this.fileUpload?.files || event?.files || [];
    const uniqueFiles = new Map<string, File>();
    files.forEach((file: File) => {
      const key = `${file.name}|${file.size}|${file.lastModified}`;
      uniqueFiles.set(key, file);
    });
    return Array.from(uniqueFiles.values());
  }

  private isFileOverSizeLimit(file: File): boolean {
    return file.size > this.maxProjectFileSizeBytes;
  }

  private showFileSizeValidation(file: File): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: `${file.name} is larger than ${this.maxProjectFileSizeLabel}. Please upload a smaller file.`
    });
  }

  getUploadStatus(file: File): 'pending' | 'uploading' | 'completed' | 'error' {
    return this.uploadFileStatuses.get(this.getUploadFileKey(file)) || 'pending';
  }

  getUploadStatusLabel(file: File): string {
    const status = this.getUploadStatus(file);
    if (status === 'uploading') return `Uploading ${this.uploadCurrentFileNumber}/${this.uploadTotalFiles}`;
    if (status === 'completed') return 'Uploaded';
    if (status === 'error') return 'Failed';
    return 'Pending';
  }

  get uploadActionLabel(): string {
    if (!this.isUploading) return 'Save';
    return `Uploading ${this.uploadCurrentFileNumber}/${this.uploadTotalFiles}`;
  }

  private getUploadFileKey(file: File): string {
    return `${file.name}|${file.size}|${file.lastModified}`;
  }

  private resetUploadProgress(): void {
    this.uploadTotalFiles = 0;
    this.uploadCurrentFileNumber = 0;
    this.uploadCompletedFiles = 0;
    this.uploadFailedFiles = 0;
    this.uploadFileStatuses.clear();
  }

  private syncFileUploadFiles(files: File[]): void {
    if (this.fileUpload) {
      (this.fileUpload as any).files = [...files];
    }
  }

  private getUploadErrorMessage(error: any): string {
    const message = typeof error === 'string'
      ? error
      : error?.error?.message || error?.message || '';

    if (error?.status === 413 || message.toLowerCase().includes('content too large')) {
      return `Each file must be ${this.maxProjectFileSizeLabel} or smaller. Please upload a smaller file.`;
    }

    if (message.toLowerCase().includes('client has disconnected') ||
      message.toLowerCase().includes('failed to read the request form') ||
      message.toLowerCase().includes('interrupted')) {
      return 'Upload was interrupted before the server received the file. Please retry.';
    }

    return message || 'Failed to upload file(s).';
  }

  // ===== Selection Methods =====
  isFileSelected(fileId: number): boolean {
    return this.selectedFileIds.has(fileId);
  }

  toggleFileSelection(fileId: number): void {
    if (this.selectedFileIds.has(fileId)) {
      this.selectedFileIds.delete(fileId);
    } else {
      this.selectedFileIds.add(fileId);
    }
  }

  isFolderFullySelected(folder: ProjectFileFolderModel): boolean {
    if (!folder.projectFileList || folder.projectFileList.length === 0) {
      return this.selectedFolderIds.has(folder.fileFolderId);
    }
    return this.selectedFolderIds.has(folder.fileFolderId)
      && folder.projectFileList.every(f => this.selectedFileIds.has(f.fileId));
  }

  isFolderPartiallySelected(folder: ProjectFileFolderModel): boolean {
    if (!folder.projectFileList || folder.projectFileList.length === 0) return false;
    const someSelected = folder.projectFileList.some(f => this.selectedFileIds.has(f.fileId));
    const allSelected = folder.projectFileList.every(f => this.selectedFileIds.has(f.fileId));
    return someSelected && !allSelected;
  }

  toggleFolderSelection(folder: ProjectFileFolderModel): void {
    if (this.isFolderFullySelected(folder)) {
      folder.projectFileList?.forEach(f => this.selectedFileIds.delete(f.fileId));
      this.selectedFolderIds.delete(folder.fileFolderId);
    } else {
      folder.projectFileList?.forEach(f => this.selectedFileIds.add(f.fileId));
      this.selectedFolderIds.add(folder.fileFolderId);
    }
  }

  get hasSelection(): boolean {
    return this.selectedFileIds.size > 0 || this.selectedFolderIds.size > 0;
  }

  clearSelection(): void {
    this.selectedFileIds.clear();
    this.selectedFolderIds.clear();
  }

  deleteSelected(): void {
    if (!this.canDelete || this.isProjectInactive || !this.hasSelection) {
      return;
    }

    const folderCount = this.selectedFolderIds.size;
    const standaloneFileCount = Array.from(this.selectedFileIds).filter(fileId => {
      return !this.folderList.some(folder =>
        this.selectedFolderIds.has(folder.fileFolderId) &&
        folder.projectFileList?.some(f => f.fileId === fileId)
      );
    }).length;

    const parts: string[] = [];
    if (folderCount > 0) parts.push(`${folderCount} folder${folderCount > 1 ? 's' : ''}`);
    if (standaloneFileCount > 0) parts.push(`${standaloneFileCount} file${standaloneFileCount > 1 ? 's' : ''}`);

    const cascade = folderCount > 0
      ? ' All files inside the selected folders will also be permanently deleted.'
      : '';

    this.confirmationService.confirm({
      header: 'Delete Items',
      icon: 'ri-error-warning-line',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'confirm-delete-btn',
      message: `Are you sure you want to delete ${parts.join(' and ')}?${cascade} This action cannot be undone.`,
      accept: () => {
        const fileIds = Array.from(this.selectedFileIds);
        const folderIds = Array.from(this.selectedFolderIds);
        this.service.bulkDeleteProjectFiles(fileIds, folderIds).subscribe({
          next: (result) => {
            if (result) {
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Selected items deleted successfully.' });
            } else {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete selected items.' });
            }
            this.clearSelection();
            this.getProjectFiles();
          },
          error: (error) => {
            console.error('Error in bulk delete:', error);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete selected items.' });
          }
        });
      }
    });
  }

  private findFolderById(folderId: number): ProjectFileFolderModel | null {
    return this.folderList.find(f => f.fileFolderId === folderId) || null;
  }

  /**
   * Handle file click to open in viewer
   */
  private readonly viewerSupportedTypes = [
    '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif',
    '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.dwg', '.dxf'
  ];

  onFileClick(file: ProjectFileModel): void {
    const fileName = file.fileName || file.fileFullName || '';
    const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    if (this.viewerSupportedTypes.includes(ext)) {
      this.router.navigate(['/files', this.projectId, 'viewer', file.fileId]);
    } else {
      this.onDownloadFile(file);
    }
  }

  onEditFileName(file: ProjectFileModel): void {
    this.editingFile = file;
    this.editFileName = file.fileName || file.fileFullName || '';
    this.showEditFileNameDialog = true;
  }

  saveEditFileName(): void {
    const newName = this.editFileName?.trim();
    if (!newName) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'File name cannot be empty.' });
      return;
    }
    if (!this.editingFile) return;

    const currentUserId = this.authService.getCurrentUser()?.userId || 0;
    const model = { ...this.editingFile, fileName: newName, updatedBy: currentUserId } as ProjectFileModel;
    this.service.updateProjectFile(model).subscribe({
      next: (result) => {
        if (result) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File name updated successfully.' });
          this.getProjectFiles();
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update file name.' });
        }
        this.showEditFileNameDialog = false;
        this.editingFile = null;
      },
      error: (error) => {
        console.error('Error updating file name:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update file name.' });
      }
    });
  }

  onDownloadFile(file: ProjectFileModel): void {
    if (!file.filePath) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'File path not available.' });
      return;
    }

    const fileName = file.fileName || file.fileFullName || 'download';
    const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    const downloadRequest = fileExtension === '.pdf' && file.fileId > 0
      ? this.service.downloadProjectFile(file.fileId)
      : this.ociService.getOciResourceEnhanced(file.filePath);

    downloadRequest.subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildProjectAssetDownloadFileName(
          this.currentProject,
          'File',
          fileName
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading file:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download file.' });
      }
    });
  }

  onDownloadFolder(folder: ProjectFileFolderModel): void {
    console.log('Download folder clicked:', folder.fileFolderId, folder.folderName);
    if (folder.fileFolderId == null || folder.fileFolderId <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Cannot download this folder.' });
      return;
    }
    this.service.downloadFolderAsZip(folder.fileFolderId).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildProjectFolderZipFileName(this.currentProject, 'Files', folder.folderName || 'Folder');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading folder:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download folder.' });
      }
    });
  }

  onDeleteFile(file: ProjectFileModel, folder: ProjectFileFolderModel): void {
    if (!this.canDelete || this.isProjectInactive) {
      return;
    }
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${file.fileName || file.fileFullName}"?`,
      accept: () => {
        console.log('Deleting file with ID:', file.fileId);
        this.service.deleteProjectFile(file.fileId).subscribe({
          next: (result) => {
            console.log('Delete result:', result);
            if (result) {
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File deleted successfully.' });
            } else {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete file.' });
            }
            this.getProjectFiles();
          },
          error: (error) => {
            console.error('Error deleting file:', error);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete file.' });
          }
        });
      }
    });
  }

}

